import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from "mobx-react-lite";
import { useTranslation } from 'react-i18next';
import { TranslationAction } from '../../helper/translationHelpers';
import { Gender, genders } from '../../../shared/definitions/other/Gender';
import { TranslationActionSelection } from './TranslationActionSelection';
const Table = styled.table.withConfig({
  displayName: "TranslationListDisplay__Table",
  componentId: "sc-qj175g-0"
})(["border-collapse:collapse;tr{border:1px solid lightgrey;}th,td{padding:5px 10px;}"]);
const Row = styled.tr.withConfig({
  displayName: "TranslationListDisplay__Row",
  componentId: "sc-qj175g-1"
})(["", ""], props => props.discarded && css(["color:#c0c0c0;"]));
var CellState;

(function (CellState) {
  CellState[CellState["Normal"] = 0] = "Normal";
  CellState[CellState["Problematic"] = 1] = "Problematic";
  CellState[CellState["Translated"] = 2] = "Translated";
})(CellState || (CellState = {}));

const Cell = styled.td.withConfig({
  displayName: "TranslationListDisplay__Cell",
  componentId: "sc-qj175g-2"
})(["", " ", " ", " ", " ", ""], props => props.proxyContent && css(["font-style:italic;color:grey;"]), props => props.state === CellState.Problematic && props.action === TranslationAction.NotSet && css(["background-color:red;"]), props => props.state === CellState.Translated && props.action === TranslationAction.NotSet && css(["background-color:lightgrey;"]), props => props.state !== CellState.Normal && props.action === TranslationAction.Commit && css(["background-color:#77ff77;"]), props => props.action === TranslationAction.Discard && css(["color:#c0c0c0;"]));
const PreviousText = styled.div.withConfig({
  displayName: "TranslationListDisplay__PreviousText",
  componentId: "sc-qj175g-3"
})(["text-decoration:line-through;"]);
const NewText = styled.div.withConfig({
  displayName: "TranslationListDisplay__NewText",
  componentId: "sc-qj175g-4"
})([""]);
const Remark = styled.div.withConfig({
  displayName: "TranslationListDisplay__Remark",
  componentId: "sc-qj175g-5"
})([""]);

const Empty = () => /*#__PURE__*/React.createElement(React.Fragment, null, "[\xA0\xA0\xA0\xA0\xA0\xA0\xA0\xA0\xA0\xA0\xA0\xA0]");

const TranslationDisplay = observer(({
  translation
}) => {
  const {
    t
  } = useTranslation();
  const {
    entityLabel,
    translatableStringLabel,
    translationsByGender,
    stillExists
  } = translation;
  const displays = new Array();
  let hasNonDiscardedSubelements = false;

  for (const gender of genders) {
    const translationDataForGender = translationsByGender.get(gender);
    const {
      currentSourceLanguageString,
      currentTargetLanguageString,
      importSourceLanguageString,
      importTargetLanguageString,
      sourceLanguageStringChanged,
      targetLanguageStringChanged,
      action,
      sourceReplacedFromNeutral
    } = translationDataForGender; // No update necessary

    if (!targetLanguageStringChanged) continue;

    if (action !== TranslationAction.Discard) {
      hasNonDiscardedSubelements = true;
    }

    let sourceState = sourceLanguageStringChanged ? CellState.Problematic : CellState.Translated;
    let targetState = targetLanguageStringChanged && currentTargetLanguageString.length > 0 ? CellState.Problematic : CellState.Translated;

    if (!stillExists) {
      sourceState = CellState.Normal;
      targetState = CellState.Normal;
    }

    displays.push( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Cell, {
      state: CellState.Normal,
      action: action
    }, t("game.character_config_gender_option" + gender)), stillExists && /*#__PURE__*/React.createElement(Cell, {
      state: sourceState,
      action: action,
      proxyContent: sourceReplacedFromNeutral
    }, sourceReplacedFromNeutral && /*#__PURE__*/React.createElement(Remark, null, t("game.character_config_gender_option" + Gender.Neutral), ":"), sourceLanguageStringChanged && /*#__PURE__*/React.createElement(PreviousText, null, importSourceLanguageString || /*#__PURE__*/React.createElement(Empty, null)), /*#__PURE__*/React.createElement(NewText, null, currentSourceLanguageString)), !stillExists && /*#__PURE__*/React.createElement(Cell, {
      state: CellState.Normal,
      action: action
    }, /*#__PURE__*/React.createElement("div", null, importSourceLanguageString || /*#__PURE__*/React.createElement(Empty, null))), /*#__PURE__*/React.createElement(Cell, {
      state: targetState,
      action: action
    }, /*#__PURE__*/React.createElement(PreviousText, null, currentTargetLanguageString), /*#__PURE__*/React.createElement(NewText, null, importTargetLanguageString)), stillExists && /*#__PURE__*/React.createElement(Cell, {
      state: CellState.Normal,
      action: action
    }, /*#__PURE__*/React.createElement(TranslationActionSelection, {
      translation: translation,
      gender: gender
    }))));
  }

  if (displays.length === 0) return null;
  const rowDiscarded = !stillExists || !hasNonDiscardedSubelements;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, {
    discarded: rowDiscarded
  }, /*#__PURE__*/React.createElement("td", {
    rowSpan: displays.length
  }, entityLabel), /*#__PURE__*/React.createElement("td", {
    rowSpan: displays.length
  }, translatableStringLabel), displays[0]), displays.slice(1).map((display, index) => /*#__PURE__*/React.createElement(Row, {
    discarded: rowDiscarded,
    key: index
  }, display)));
});
export const TranslationListDisplay = observer(({
  titleKey,
  translations,
  translationData
}) => {
  const {
    t
  } = useTranslation();
  const {
    sourceLanguageKey,
    targetLanguageKey
  } = translationData;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", null, t(titleKey)), /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    colSpan: 3
  }), /*#__PURE__*/React.createElement("th", null, t("translation.language_" + sourceLanguageKey)), /*#__PURE__*/React.createElement("th", null, t("translation.language_" + targetLanguageKey))), translations.map(translation => /*#__PURE__*/React.createElement(TranslationDisplay, {
    key: translation.translatedStringModelId,
    translation: translation
  })))));
});